import type {
	Auth,
	AuthProvider,
	UserCredential,
} from 'firebase/auth';
import {
	EmailAuthProvider,
	confirmPasswordReset,
	reauthenticateWithCredential,
	signInWithEmailAndPassword,
	signInWithPopup,
	updatePassword,
} from 'firebase/auth';

class FirebaseAuthHandler {
	constructor(private readonly auth: Auth) {}

	async getAuth() {
		return this.auth;
	}

	async signInWithEmail(email: string, password: string) {
		try {
			const userCredential = await signInWithEmailAndPassword(this.auth, email, password);

			return this.getUserAndIDToken(userCredential);
		}
		catch (error) {
			const { message } = error as Error;
			throw new Error(`Failed to sign in with email and password: ${message}`);
		}
	}

	async signInWithProvider(provider: AuthProvider) {
		try {
			const userCredential = await signInWithPopup(this.auth, provider);

			return this.getUserAndIDToken(userCredential);
		}
		catch (error) {
			const { message } = error as Error;
			throw new Error(`Failed to sign in with Google: ${message}`);
		}
	}

	async updatePassword(email:string, password: string, newPassword: string) {
		try {
			const credential = EmailAuthProvider.credential(email, password);
			const { currentUser } = this.auth;

			if (!currentUser) throw new Error('User not found');

			await reauthenticateWithCredential(currentUser, credential);
			await updatePassword(currentUser, newPassword);
		}
		catch (error) {
			const { message } = error as Error;
			throw new Error(`Failed to update password: ${message}`);
		}
	}

	async resetPassword(oobCode: string, newPassword: string) {
		try {
			await confirmPasswordReset(this.auth, oobCode, newPassword);
		}
		catch (error) {
			const { message } = error as Error;
			throw new Error(`Failed to reset password: ${message}`);
		}
	}

	async logout() {
		await this.auth.signOut();
	}

	private async getUserAndIDToken(userCredential: UserCredential) {
		const { user } = userCredential;
		const idToken = await user.getIdToken();

		return {
			user,
			idToken,
		};
	}
}

export default FirebaseAuthHandler;